import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'EcommerceFree',
  data () {
    return {
      isLoading: {
        analyzer: false,
        user: false,
        email: false
      },

      domain: null,
      domainRules: [(value) => !!value || 'Domain is required', (value) => !value || this.isValidDomain(value) || 'Invalid domain format'],
      email: null,
      emailSent: false,
      emailSaved: false,
      emailRules: [(value) => !!value || 'Email is required', (value) => !value || /.+@.+\..+/.test(value) || 'Invalid email'],

      form: null,
      contentplanId: null,
      workspaceId: null,
      socket: null,
      analysisProgress: {
        getCategoryPages: null,
        crawlCategoryPages: null,
        suggestNewTitles: null,
        createCsv: null
      },
      currentStepMessage: '',
      hasError: false,
      errorMessage: ''
    }
  },
  computed: {
    ...mapGetters(['websocketUrl']),
    allStatusesNull () {
      return Object.values(this.analysisProgress).every((status) => status === null)
    },
    allStatusesDone () {
      return Object.values(this.analysisProgress).every((status) => status === 'done')
    },
    domainHint () {
      if (!this.domain) return 'Enter your ecommerce store domain (e.g., mystore.com or https://mystore.com)'
      if (!this.isValidDomain(this.domain)) return 'Please enter a valid domain'
      return ''
    }
  },
  methods: {
    ...mapActions(['postUser', 'sendEmail']),
    isValidDomain (domain) {
      try {
        // Allow domain with or without protocol
        const urlToTest = domain.startsWith('http') ? domain : `https://${domain}`
        const url = new URL(urlToTest)
        return url.hostname.includes('.')
      } catch (e) {
        return false
      }
    },
    async runAnalyzer () {
      try {
        if (!this.domain?.length) return

        this.isLoading.analyzer = true
        this.currentStepMessage = 'Starting analysis...'

        // Start websocket connection and trigger analysis directly
        await this.connectWebSocketAndTriggerAnalysis()
      } catch (err) {
        console.log(err)
        this.isLoading.analyzer = false
      }
    },
    async connectWebSocketAndTriggerAnalysis () {
      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
        console.log('WebSocket connection not established, opening...')
        this.socket = new WebSocket(this.websocketUrl)
      }

      this.socket.onopen = () => {
        console.log('WebSocket connection established')
        // Trigger the ecommerce analyzer via websocket
        this.sendMessage({
          action: 'runEcommerce',
          message: {
            domain: this.domain
          }
        })
      }

      // Handle incoming messages
      this.socket.onmessage = (event) => {
        console.log('Message received:', event)
        this.websocketMessageHandler(event)
      }

      this.socket.onerror = (error) => {
        console.error('WebSocket error: ', error.message)
      }

      this.socket.onclose = () => {
        console.log('WebSocket connection closed')
      }
    },
    async sendMessage (body) {
      try {
        if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
          console.log('WebSocket connection not established, opening...')
          await this.connectWebSocketAndTriggerAnalysis()
        } else {
          console.log('WebSocket connection open, sending message...')
          this.socket.send(JSON.stringify(body))
        }
      } catch (err) {
        console.error(err)
      }
    },
    async websocketMessageHandler (event) {
      const eventData = JSON.parse(event.data)
      const messages = eventData?.messages
      if (!messages) return
      const messageData = messages[0]
      if (!messageData) return
      const { stepName, status, message } = messageData

      console.log('WebSocket message:', { stepName, status, message })

      // Update current step message
      this.currentStepMessage = message || ''

      // Capture contentplanId from first tracker message
      if (messageData.contentplanId && !this.contentplanId) {
        this.contentplanId = messageData.contentplanId
        this.workspaceId = 1 // ecommerce analyzer always uses workspace 1
      }

      // Update progress based on step
      if (stepName === 'getCategoryPages') {
        if (status === 'inProgress') {
          this.analysisProgress.getCategoryPages = 'inProgress'
        } else if (status === 'done') {
          // Infer crawl start since backend doesn't emit it
          if (this.analysisProgress.crawlCategoryPages !== 'done') {
            this.analysisProgress.getCategoryPages = 'done'
            this.analysisProgress.crawlCategoryPages = 'inProgress'
          }
        }
      }

      // No explicit crawlCategoryPages messages are emitted; status is inferred

      if (stepName === 'suggestNewTitles') {
        // Ensure previous steps are closed
        this.analysisProgress.getCategoryPages = 'done'
        this.analysisProgress.crawlCategoryPages = 'done'
        if (status === 'inProgress') {
          this.analysisProgress.suggestNewTitles = 'inProgress'
        }
      }

      if (stepName === 'createCsv') {
        this.analysisProgress.suggestNewTitles = 'done'
        if (status === 'inProgress') {
          this.analysisProgress.createCsv = 'inProgress'
        } else if (status === 'done') {
          this.analysisProgress.createCsv = 'done'
          // Analysis flow complete
          this.isLoading.analyzer = false
        }
      }

      // Handle errors
      if (status === 'error') {
        this.isLoading.analyzer = false
        this.hasError = true
        this.errorMessage = message || 'An unexpected error occurred during analysis'
        this.currentStepMessage = ''
        // Reset all progress states
        this.analysisProgress = {
          getCategoryPages: null,
          crawlCategoryPages: null,
          suggestNewTitles: null,
          createCsv: null
        }
      }
    },
    async addNewUser () {
      this.isLoading.user = true
      let userId
      try {
        userId = await this.postUser({
          contentplanId: this.contentplanId,
          email: this.email
        })
      } catch (err) {
        console.log(err)
      }
      this.emailSaved = true
      this.isLoading.user = false
      return userId
    },
    async addNewUserAndSendEmail () {
      this.isLoading.email = true
      try {
        const userId = await this.addNewUser()
        await this.sendEmail({
          action: 'ecommerce',
          userId,
          contentplanId: this.contentplanId
        })
      } catch (err) {
        console.log(err)
      }
      this.emailSent = true
      this.isLoading.email = false
    },
    reset () {
      this.domain = null
      this.email = null
      this.contentplanId = null
      this.workspaceId = null
      this.emailSent = false
      this.emailSaved = false
      this.currentStepMessage = ''
      this.hasError = false
      this.errorMessage = ''
      this.analysisProgress = {
        getCategoryPages: null,
        crawlCategoryPages: null,
        suggestNewTitles: null,
        createCsv: null
      }
      if (this.socket) {
        this.socket.close()
        this.socket = null
      }
    }
  },
  async mounted () {},
  beforeDestroy () {
    // Clean up websocket when component is destroyed
    if (this.socket) {
      this.socket.close()
    }
  }
}
